/**
 * Marker system public API.
 * Delegates to 2D or 3D renderer based on the current view mode and renders
 * markers partitioned into named layers (contacts, CoT, and feed tracks).
 */

import type { TakContact, NormalizedSaEvent, Track } from '@vtak/shared';
import type { ViewMode, MapInstance } from '../map-setup';
import { lerpPosition, toLatLng } from '../animation';
import {
  contactsToMarkerData,
  eventsToMarkerData,
  tracksToMarkerData,
  computeDiff,
  applyDiff,
  clearRegistry,
} from './marker-state';
import { cullMarkersForViewport } from './viewport-cull';
import {
  initMarkers2D,
  applyLayerDiff2D,
  setLayerPositions2D,
  setLayerVisibility2D,
  destroyMarkers2D,
} from './marker-2d';
import {
  initMarkers3D,
  applyLayerDiff3D,
  setLayerPositions3D,
  setLayerVisibility3D,
  destroyMarkers3D,
} from './marker-3d';

/** Total animation duration in milliseconds */
const ANIMATION_DURATION_MS = 100;

/** Built-in layer keys. */
const CONTACT_LAYER = 'contact';
const COT_LAYER = 'cot';

/** Initialize the marker system for the given map and mode */
export function setupMarkers(map: MapInstance, mode: ViewMode): void {
  if (mode === '2d') {
    initMarkers2D(map as google.maps.Map);
  } else {
    initMarkers3D(map as google.maps.maps3d.Map3DElement);
  }
}

/** Update contacts on the map */
export function updateContacts(map: MapInstance, mode: ViewMode, contacts: TakContact[]): void {
  const incoming = contactsToMarkerData(contacts);
  const diff = computeDiff(incoming, CONTACT_LAYER);
  applyDiff(diff, incoming);
  applyLayerDiff(map, mode, CONTACT_LAYER, diff);
}

/** Animate CoT marker updates with smooth position interpolation */
export function animateCotUpdate(
  map: MapInstance,
  mode: ViewMode,
  events: NormalizedSaEvent[],
  previousPositions: Map<string, [number, number]>,
): void {
  const incoming = eventsToMarkerData(events);
  const diff = computeDiff(incoming, COT_LAYER);
  applyDiff(diff, incoming);
  applyLayerDiff(map, mode, COT_LAYER, diff);
  animateLayer(mode, COT_LAYER, events, previousPositions);
}

/** Render a feed track layer (aircraft, vessels, fires, …) with animation */
export function renderTrackLayer(
  map: MapInstance,
  mode: ViewMode,
  layerId: string,
  tracks: Track[],
  previousPositions: Map<string, [number, number]>,
): void {
  const incoming = cullMarkersForViewport(tracksToMarkerData(tracks, layerId), layerId);
  const diff = computeDiff(incoming, layerId);
  applyDiff(diff, incoming);
  applyLayerDiff(map, mode, layerId, diff);
  animateLayer(mode, layerId, tracks, previousPositions);
}

/** Show or hide a rendered layer */
export function setLayerVisibility(mode: ViewMode, layerId: string, visible: boolean): void {
  if (mode === '2d') {
    setLayerVisibility2D(layerId, visible);
  } else {
    setLayerVisibility3D(layerId, visible);
  }
}

/** Destroy all markers and clean up */
export function destroyMarkers(mode: ViewMode): void {
  clearRegistry();
  cancelAllAnimations();
  if (mode === '2d') {
    destroyMarkers2D();
  } else {
    destroyMarkers3D();
  }
}

/* ─── Internal ─── */

/** Active animation frame id per layer, used to cancel stale animations */
const activeAnimFrames = new Map<string, number>();

/** Apply a marker diff to the active renderer for a layer */
function applyLayerDiff(
  map: MapInstance,
  mode: ViewMode,
  layer: string,
  diff: ReturnType<typeof computeDiff>,
): void {
  if (mode === '2d') {
    applyLayerDiff2D(map as google.maps.Map, layer, diff);
  } else {
    applyLayerDiff3D(map as google.maps.maps3d.Map3DElement, layer, diff);
  }
}

/** Animate a layer's marker positions toward their targets */
function animateLayer(
  mode: ViewMode,
  layer: string,
  events: NormalizedSaEvent[],
  previousPositions: Map<string, [number, number]>,
): void {
  cancelAnimation(layer);
  const start = performance.now();

  function tick(now: number): void {
    const t = Math.min((now - start) / ANIMATION_DURATION_MS, 1);
    const positions = interpolateAll(events, t, previousPositions);
    setLayerPositions(mode, layer, positions);

    if (t < 1) {
      activeAnimFrames.set(layer, requestAnimationFrame(tick));
    } else {
      activeAnimFrames.delete(layer);
    }
  }

  activeAnimFrames.set(layer, requestAnimationFrame(tick));
}

/** Set marker positions for a layer on the active renderer */
function setLayerPositions(
  mode: ViewMode,
  layer: string,
  positions: Map<string, google.maps.LatLngLiteral>,
): void {
  if (mode === '2d') {
    setLayerPositions2D(layer, positions);
  } else {
    setLayerPositions3D(layer, positions);
  }
}

/** Cancel a layer's running animation frame */
function cancelAnimation(layer: string): void {
  const frame = activeAnimFrames.get(layer);
  if (frame) {
    cancelAnimationFrame(frame);
    activeAnimFrames.delete(layer);
  }
}

/** Cancel all running animation frames */
function cancelAllAnimations(): void {
  for (const frame of activeAnimFrames.values()) cancelAnimationFrame(frame);
  activeAnimFrames.clear();
}

/** Interpolate every event's position between its previous and target point */
function interpolateAll(
  events: NormalizedSaEvent[],
  t: number,
  previousPositions: Map<string, [number, number]>,
): Map<string, google.maps.LatLngLiteral> {
  const result = new Map<string, google.maps.LatLngLiteral>();

  for (const evt of events) {
    const target: [number, number] = [evt.lon, evt.lat];
    const prev = previousPositions.get(evt.uid) ?? target;
    result.set(evt.uid, toLatLng(lerpPosition(prev, target, t)));
  }

  return result;
}
